import React, { useState, useEffect } from 'react';
import { listarPagosAdmin } from '../../services/pagos';
import { listarUsuarios } from '../../services/usuario';
import '../../styles/admin/AdminPagos.css';

function AdminPagos() {
  const [pagos, setPagos] = useState([]);
  const [totalIngresos, setTotalIngresos] = useState(0);
  const [totalPagos, setTotalPagos] = useState(0);
  const [promedio, setPromedio] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filtros
  const [usuarioId, setUsuarioId] = useState('');
  const [emailBusqueda, setEmailBusqueda] = useState('');
  const [estado, setEstado] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(20);

  // Cargar lista de usuarios para el selector
  useEffect(() => {
    const cargarUsuarios = async () => {
      try {
        const data = await listarUsuarios(0, 1000);
        setUsuarios(data);
      } catch (err) {
        console.error('Error cargando usuarios:', err);
      }
    };
    cargarUsuarios();
  }, []);

  const cargarPagos = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        skip: skip,
        limit: limit
      };
      
      if (usuarioId) params.usuarioId = parseInt(usuarioId);
      if (emailBusqueda.trim()) params.email = emailBusqueda.trim();
      if (estado) params.estado = estado;
      if (fechaDesde) params.fechaDesde = fechaDesde;
      if (fechaHasta) params.fechaHasta = fechaHasta;

      const data = await listarPagosAdmin(params);
      setPagos(data.pagos || []);
      setTotalIngresos(data.total_ingresos || 0);
      setTotalPagos(data.total || 0);
      setPromedio(data.promedio || 0);
    } catch (err) {
      setError('Error al cargar los pagos: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Cargar al cambiar filtros o paginación
  useEffect(() => {
    cargarPagos();
  }, [usuarioId, emailBusqueda, estado, fechaDesde, fechaHasta, skip, limit]);

  const handleBuscar = () => {
    setSkip(0);
    cargarPagos();
  };

  const handleLimpiarFiltros = () => {
    setUsuarioId('');
    setEmailBusqueda('');
    setEstado('');
    setFechaDesde('');
    setFechaHasta('');
    setSkip(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `$${Number(amount).toFixed(2)}`;
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      'completed': { label: '✅ Completado', class: 'estado-completed' },
      'pending': { label: '⏳ Pendiente', class: 'estado-pending' },
      'cancelled': { label: '❌ Cancelado', class: 'estado-cancelled' },
      'failed': { label: '❌ Fallido', class: 'estado-failed' }
    };
    return estados[estado] || { label: estado, class: '' };
  };

  return (
    <div className="admin-pagos-container">
      <div className="admin-pagos-header">
        <h1>💰 Historial de Pagos</h1>
        <p>Consulta todas las transacciones realizadas en el sistema</p>
      </div>

      {/* RESUMEN */}
      <div className="admin-pagos-resumen">
        <div className="resumen-card">
          <span className="resumen-label">Total de ingresos</span>
          <span className="resumen-valor">{formatCurrency(totalIngresos)}</span>
        </div>
        <div className="resumen-card">
          <span className="resumen-label">Total de pagos</span>
          <span className="resumen-valor">{totalPagos}</span>
        </div>
        <div className="resumen-card">
          <span className="resumen-label">Promedio por pago</span>
          <span className="resumen-valor">{formatCurrency(promedio)}</span>
        </div>
      </div>

      {/* FILTROS */}
      <div className="admin-pagos-filtros">
        <div className="filtro-group">
          <label>Usuario</label>
          <select
            value={usuarioId}
            onChange={(e) => setUsuarioId(e.target.value)}
          >
            <option value="">Todos los usuarios</option>
            {usuarios.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nombre} 
              </option>
            ))}
          </select>
        </div>

        <div className="filtro-group">
          <label>Buscar por email</label>
          <input
            type="text"
            placeholder="Email del usuario..."
            value={emailBusqueda}
            onChange={(e) => setEmailBusqueda(e.target.value)}
          />
        </div>

        <div className="filtro-group">
          <label>Estado</label>
          <select
            value={estado}
            onChange={(e) => setEstado(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="completed">Completado</option>
            <option value="pending">Pendiente</option>
            <option value="cancelled">Cancelado</option>
            <option value="failed">Fallido</option>
          </select>
        </div>

        <div className="filtro-group">
          <label>Fecha desde</label>
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
          />
        </div>

        <div className="filtro-group">
          <label>Fecha hasta</label>
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
          />
        </div>

        <div className="filtro-actions">
          <button className="btn-buscar" onClick={handleBuscar}>
            🔍 Buscar
          </button>
          <button className="btn-limpiar" onClick={handleLimpiarFiltros}>
            Limpiar
          </button>
        </div>
      </div>

      {/* TABLA */}
      <div className="admin-pagos-table-wrapper">
        {loading ? (
          <div className="admin-pagos-loading">Cargando pagos...</div>
        ) : error ? (
          <div className="admin-pagos-error">
            <p>{error}</p>
            <button onClick={cargarPagos}>Reintentar</button>
          </div>
        ) : pagos.length === 0 ? (
          <div className="admin-pagos-empty">
            <p>No hay pagos para los filtros seleccionados.</p>
          </div>
        ) : (
          <>
            <table className="admin-pagos-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Usuario</th>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Monto</th>
                  <th>Créditos</th>
                  <th>Estado</th>
                  <th>Transacción</th>
                </tr>
              </thead>
              <tbody>
                {pagos.map((pago) => {
                  const estadoInfo = getEstadoBadge(pago.estado);
                  return (
                    <tr key={pago.id}>
                      <td>{formatDate(pago.fecha_creacion)}</td>
                      <td>{pago.usuario_nombre || 'N/A'}</td>
                      <td>{pago.usuario_email || 'N/A'}</td>
                      <td>
                        <span className="plan-badge">
                          {pago.plan === 'basico' ? '📘 Básico' :
                           pago.plan === 'intermedio' ? '📗 Intermedio' :
                           pago.plan === 'premium' ? '📕 Premium' : pago.plan}
                        </span>
                      </td>
                      <td className="monto-col">{formatCurrency(pago.monto)}</td>
                      <td>{pago.creditos}</td>
                      <td>
                        <span className={`estado-badge ${estadoInfo.class}`}>
                          {estadoInfo.label}
                        </span>
                      </td>
                      <td className="transaction-id">
                        <span title={pago.transaction_id || pago.payment_id}>
                          {pago.transaction_id ? pago.transaction_id.slice(0, 12) + '...' : 'N/A'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* PAGINACIÓN */}
            <div className="admin-pagos-paginacion">
              <span>
                Mostrando {skip + 1} - {Math.min(skip + limit, totalPagos)} de {totalPagos}
              </span>
              <div className="paginacion-buttons">
                <button
                  onClick={() => setSkip(Math.max(0, skip - limit))}
                  disabled={skip === 0}
                >
                  ← Anterior
                </button>
                <button
                  onClick={() => setSkip(skip + limit)}
                  disabled={skip + limit >= totalPagos}
                >
                  Siguiente →
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminPagos;